import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { NavyogaLogo } from "./NavyogaLogo";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Therapies", href: "#therapies" },
  { label: "Schedule", href: "#schedule" },
  { label: "Gallery", href: "#classes" },
  { label: "Awards", href: "#awards" },
  { label: "Community", href: "#stories" },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToSection(href), open ? 250 : 0);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-emerald-900/10 bg-white/85 shadow-[0_8px_30px_-12px_rgba(30,58,138,0.15)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-3"
          aria-label="Back to top"
        >
          <NavyogaLogo className="h-11" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(link.href)}
              className="group relative text-sm font-medium text-slate-700 transition-colors duration-300 hover:text-[#2E8B57]"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#2E8B57] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-book-consultation-button"
            onClick={() => go("#contact")}
            className="flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_25px_-10px_rgba(30,58,138,0.6)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#2E8B57]"
          >
            <CalendarCheck className="h-4 w-4" />
            Book Consultation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="rounded-full p-2 text-slate-800 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-emerald-900/10 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(link.href)}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-slate-800 transition-colors hover:bg-emerald-50 hover:text-[#2E8B57]"
                >
                  {link.label}
                </motion.button>
              ))}
              <button
                data-testid="nav-mobile-book-button"
                onClick={() => go("#contact")}
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-3 text-sm font-semibold text-white"
              >
                <CalendarCheck className="h-4 w-4" />
                Book Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
